import Vue from 'vue'
import Order from '../models/Shop/Order'
import ParcelMachine from '../models/Shop/ParcelMachine'

export const state = () => ({
  order: null,
  checkout_order: null,
  orders: []
})

export const getters = {
  cartItems: (state) => {
    return state.order.items
  },
  getOrder: (state) => {
    return state.order
  },
  getCheckoutOrder: (state) => {
    return state.checkout_order
  },
  getOrders: (state) => {
    return state.orders
  }
}

export const mutations = {
  addToCart: (state, product, price) => {
    const cartItem = state.order.items.find(item => item.id === product.id && item.units === price.units)
    if (cartItem) {
      Object.assign(cartItem, { ...product, units: price.units, price: price.price, discount: product.discount })
    } else {
      state.order.items.push({ ...product, units: price.units, price: price.price, discount: product.discount })
    }
  },
  incrementCartItemQuantity: (state, payload) => {
    const cartItem = state.order.items.find(item => item.id === payload.id)
    if (cartItem) {
      Object.assign(cartItem, payload)
    } else {
      state.order.items.push(payload)
    }
  },
  changeCartItemQuantity: (state, payload) => {
    const cartItem = state.order.items.find(item => item.id === payload.id)
    if (cartItem) {
      Object.assign(cartItem, payload)
    } else {
      state.order.items.push(payload)
    }
  },
  removeCartItem: (state, payload) => {
    const index = state.order.items.indexOf(payload)
    state.order.items.splice(index, 1)
  },
  setOrder: (state, payload) => {
    state.order = payload
  },
  resetOrder: (state) => {
    state.order = { items: [] }
  },
  setCheckoutOrder: (state, payload) => {
    state.checkout_order = payload
  },
  setOrders: (state, payload) => {
    state.orders = payload
  }
}

export const actions = {
  async fetchCart ({ commit }, payload) {
    try {
      if (!payload) {
        payload = {}
      }
      payload.session = this.$storage.getUniversal('guest-session-id')
      const result = await Order.custom('orders/cart')
        .include('items', 'items.product', 'items.product.media', 'items.product.category', 'items.product.unit')
        .params({
          session: payload.session
        })
        .first()
      commit('setOrder', result)
    } catch (error) {
    }
  },
  async addToCart ({ dispatch }, payload) {
    try {
      payload.session = this.$storage.getUniversal('guest-session-id')
      const result = await this.$axios.post(`/orders/add-to-cart/${payload.id}`, payload)
      if (result) {
        dispatch('fetchCart')
      }
    } catch (error) {
      if (process.client) {
        Vue.notify({
          group: 'general',
          type: 'error',
          text: this.app.i18n.t('errors.failed_add_to_cart')
        })
      }
    }
  },
  async incrementCartItemQuantity ({ dispatch }, payload) {
    try {
      payload.session = this.$storage.getUniversal('guest-session-id')
      const result = await this.$axios.put(`/orders/increment-quantity/${payload.item.id}`, payload)
      if (result) {
        dispatch('fetchCart')
      }
    } catch (error) {
      if (process.client) {
        Vue.notify({
          group: 'general',
          type: 'error',
          text: this.app.i18n.t('errors.failed_to_change_cart_item_quantity')
        })
      }
    }
  },
  async changeCartItemQuantity ({ dispatch }, payload) {
    try {
      payload.session = this.$storage.getUniversal('guest-session-id')
      const result = await this.$axios.put(`/orders/change-quantity/${payload.item.id}`, payload)
      if (result) {
        dispatch('fetchCart')
      }
    } catch (error) {
      if (process.client) {
        Vue.notify({
          group: 'general',
          type: 'error',
          text: this.app.i18n.t('errors.failed_to_change_cart_item_quantity')
        })
      }
    }
  },
  async removeCartItem ({ dispatch }, payload) {
    try {
      payload.session = this.$storage.getUniversal('guest-session-id')
      const result = await this.$axios.delete(`/orders/remove-from-cart/${payload.item.id}`, { data: payload })
      if (result) {
        dispatch('fetchCart')
      }
    } catch (error) {
      if (process.client) {
        Vue.notify({
          group: 'general',
          type: 'error',
          text: this.app.i18n.t('errors.failed_to_remove_cart_item')
        })
      }
    }
  },
  async checkout ({ commit }, payload) {
    try {
      const result = await this.$axios.post('/orders/checkout', payload)
      if (result && result.data && result.data.data) {
        const checkout_order = await Order
          .include('items', 'items.product', 'items.product.unit', 'items.product.media', 'items.product.category', 'addresses', 'discount', 'parcelMachine')
          .find(result.data.data.id)
        commit('setCheckoutOrder', checkout_order.data)
      }
      commit('resetOrder')
      this.$auth.fetchUser()
      this.$router.push(this.localePath('/account/order-success'))
    } catch (error) {
      if (process.client) {
        Vue.notify({
          group: 'general',
          type: 'error',
          text: this.app.i18n.t('errors.checkout_failed')
        })
      }
    }
  },
  async getOrders ({ commit }, payload) {
    try {
      const result = await this.$axios.get('/orders', payload)
      commit('setOrders', result.data.data)
      return result.data.data
    } catch (error) {
      if (process.client) {
        Vue.notify({
          group: 'general',
          type: 'error',
          text: this.app.i18n.t('not_found')
        })
      }
    }
  },
  async fetchOrder ({ commit }, payload) {
    try {
      const result = await Order
        .include('items', 'items.product', 'items.product.media', 'items.product.category', 'addresses', 'discount', 'parcelMachine', 'items.product.unit')
        .find(payload)
      return result.data
    } catch (error) {
      if (process.client) {
        Vue.notify({
          group: 'general',
          type: 'error',
          text: this.app.i18n.t('not_found')
        })
      }
    }
  },
  async cancelOrder ({ commit, dispatch }, payload) {
    try {
      await this.$axios.post(`/orders/${payload.id}/cancel`, payload)
      if (process.client) {
        Vue.notify({
          group: 'general',
          type: 'success',
          text: this.app.i18n.t('order_canceled')
        })
      }
      dispatch('getOrders')
    } catch (error) {
      if (process.client) {
        Vue.notify({
          group: 'general',
          type: 'error',
          text: this.app.i18n.t('errors.failed_to_cancel_order')
        })
      }
    }
  },
  async confirmDiscountCode ({ dispatch }, payload) {
    try {
      payload.session = this.$storage.getUniversal('guest-session-id')
      const result = await this.$axios.post('/orders/confirm-discount-code', payload)
      if (result) {
        if (process.client) {
          Vue.notify({
            group: 'general',
            type: 'success',
            text: this.app.i18n.t('discount_found')
          })
        }
        dispatch('fetchCart')
      }
    } catch (error) {
      if (process.client) {
        Vue.notify({
          group: 'general',
          type: 'error',
          text: this.app.i18n.t('discount_not_found')
        })
      }
    }
  },
  async fetchParcelMachines ({}, payload) {
    try {
      const parcel_machines = ParcelMachine.include('country')
      if (payload) {
        if (payload.country_id) {
          parcel_machines.where('country_id', payload.country_id)
        }
      }
      const result = await parcel_machines.get()
      return result
    } catch (error) {
      if (process.client) {
        Vue.notify({
          group: 'general',
          type: 'error',
          text: this.app.i18n.t('not_found')
        })
      }
    }
  },
  async changeDeliveryType ({ dispatch }, payload) {
    try {
      payload.session = this.$storage.getUniversal('guest-session-id')
      const result = await this.$axios.put('/orders/change-delivery-type', payload)
      if (result) {
        dispatch('fetchCart')
      }
    } catch (error) {
      if (process.client) {
        Vue.notify({
          group: 'general',
          type: 'error',
          text: this.app.i18n.t('errors.failed_to_change_order_delivery_type')
        })
      }
    }
  },
  async invoice ({ commit, dispatch }, payload) {
    try {
      const result = await this.$axios.post(`/orders/${payload.id}/invoice`, {},
        {
          responseType: 'arraybuffer',
          headers: {
            Accept: 'application/pdf'
          }
        })
      return result
    } catch (error) {
      if (process.client) {
        Vue.notify({
          group: 'general',
          type: 'error',
          text: this.app.i18n.t('errors.failed_invoice_down')
        })
      }
    }
  },
  resetCart ({ commit }) {
    try {
      commit('resetOrder')
    } catch (error) {
    }
  }
}
